import type { DnscPage, DnscRequirement } from '../api/overview'
import type { AssessmentAnswer, AssessmentRequirement, AssessmentScore, AssuranceLevel, EvidenceFile } from '../api/dnscAssessment'
import type { PostValue } from '../api/client'
import { FUNCTIONS, toneOf, type Tone } from './dnsc'

/**
 * Socotelile filei "DNSC self-assessment" (`Nis2Assessment`), fara React.
 *
 * Calculul DNSC NU se reface aici: scorurile (subcategorie, categorie, total,
 * masuri cheie) vin gata de la server (`DnscScore`, probat pe formulele
 * xlsx-urilor). Aici sunt doar intrebarile ecranului: ce e pe formularul
 * nivelului, ce mai e de raspuns, ce propune platforma, ce se trimite la salvare.
 */

/** Ce vede un rol fara dreptul autoevaluarii (403) - pe fila DNSC si pe Summary. */
export const NO_ASSESSMENT_RIGHT =
  'Your role cannot open the DNSC self-assessment. It shows the gaps of the organisation (TLP: AMBER), so it has its own right: NIS2 - DNSC self-assessment (read to see it, edit to answer).'

export const LEVEL_ORDER: Record<AssuranceLevel, number> = { B: 1, I: 2, E: 3 }
export const LEVEL_NAMES: Record<AssuranceLevel, string> = { B: 'Basic', I: 'Important', E: 'Essential' }

export function onForm(r: AssessmentRequirement, level: AssuranceLevel): boolean {
  return LEVEL_ORDER[r.level] <= LEVEL_ORDER[level]
}

export const isKeyAt = (r: AssessmentRequirement, level: AssuranceLevel) => r.key_levels.includes(level)
export const isManagementAt = (r: AssessmentRequirement, level: AssuranceLevel) => r.management_levels.includes(level)
export const naAllowedAt = (r: AssessmentRequirement, level: AssuranceLevel) => r.na_levels.includes(level)

export type AnswerState = 'todo' | 'partial' | 'done' | 'na'

export function answerState(a: AssessmentAnswer | null | undefined): AnswerState {
  if (!a) return 'todo'
  if (a.not_applicable) return 'na'
  if (a.doc_score !== null && a.impl_score !== null) return 'done'
  if (a.doc_score !== null || a.impl_score !== null) return 'partial'
  return 'todo'
}

/** Tonul unei medii (2,95): pe aceeasi scara ca nota, dupa partea intreaga. */
export function toneOfAverage(n: number | null | undefined, target?: number | null): Tone {
  if (n === null || n === undefined) return 'grey'
  if (target !== null && target !== undefined) return n >= target ? 'green' : n >= target - 0.5 ? 'orange' : 'red'
  return toneOf(Math.floor(n))
}

/** Doua zecimale, cum le arata foaia de sumar. */
export function formatScore(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—'
  return (Math.round(n * 100) / 100).toFixed(2)
}

/** Propunerile din date (`/overview/nis2/dnsc`), pe ID-ul cerintei. */
export function proposalsById(page: DnscPage | undefined): Map<string, DnscRequirement> {
  const out = new Map<string, DnscRequirement>()
  if (page && page.ready && Array.isArray(page.requirements)) {
    for (const r of page.requirements) out.set(r.id, r)
  }
  return out
}

export type StatusFilter = 'all' | 'todo' | 'answered' | 'below'
export type IntroducedFilter = 'all' | AssuranceLevel

export interface AssessmentFilters {
  status: StatusFilter
  keyOnly: boolean
  /** Cerintele care intra la nivelul dat (B / I / E), din formularul firmei. */
  introduced: IntroducedFilter
  fn: string
}

export const NO_ASSESSMENT_FILTERS: AssessmentFilters = { status: 'all', keyOnly: false, introduced: 'all', fn: 'all' }

/** O cerinta e "sub tinta" cand e masura cheie sub tinta sau sta intr-o categorie sub tinta. */
export function belowTarget(r: AssessmentRequirement, score: AssessmentScore | null | undefined): boolean {
  if (!score) return false
  return score.targets.key_measures_below.includes(r.code) || score.targets.categories_below.includes(r.category)
}

export function applyAssessmentFilters(
  requirements: AssessmentRequirement[],
  level: AssuranceLevel,
  f: AssessmentFilters,
  score: AssessmentScore | null | undefined,
): AssessmentRequirement[] {
  return requirements.filter((r) => {
    if (!onForm(r, level)) return false
    if (f.introduced !== 'all' && r.level !== f.introduced) return false
    if (f.keyOnly && !isKeyAt(r, level)) return false
    if (f.fn !== 'all' && r.function !== f.fn) return false
    const state = answerState(r.answer)
    if (f.status === 'todo' && (state === 'done' || state === 'na')) return false
    if (f.status === 'answered' && !(state === 'done' || state === 'na')) return false
    if (f.status === 'below' && !belowTarget(r, score)) return false
    return true
  })
}

export interface FunctionProgress {
  code: string
  name: string
  asked: number
  answered: number
  score: number | null
}

/** Pe functii, in ordinea NIST: cate sunt pe formular, cate au raspuns intreg, media categoriilor. */
export function progressByFunction(
  requirements: AssessmentRequirement[],
  level: AssuranceLevel,
  score: AssessmentScore | null | undefined,
): FunctionProgress[] {
  return FUNCTIONS.map((fn) => {
    const mine = requirements.filter((r) => r.function === fn.code && onForm(r, level))
    const answered = mine.filter((r) => ['done', 'na'].includes(answerState(r.answer))).length
    return { code: fn.code, name: fn.name, asked: mine.length, answered, score: score?.functions[fn.code]?.score ?? null }
  }).filter((p) => p.asked > 0)
}

/** Grupele ecranului: functie -> categorie -> cerinte, in ordinea catalogului. */
export function groupByCategory(requirements: AssessmentRequirement[]): Array<{
  fn: { code: string; name: string }
  categories: Array<{ code: string; name: string; items: AssessmentRequirement[] }>
}> {
  return FUNCTIONS.map((fn) => {
    const mine = requirements.filter((r) => r.function === fn.code)
    const categories: Array<{ code: string; name: string; items: AssessmentRequirement[] }> = []
    for (const r of mine) {
      let cat = categories.find((c) => c.code === r.category)
      if (!cat) {
        cat = { code: r.category, name: r.category_name, items: [] }
        categories.push(cat)
      }
      cat.items.push(r)
    }
    return { fn, categories }
  }).filter((g) => g.categories.length > 0)
}

/** Ce scrie omul in formularul unei cerinte. */
export interface AnswerDraft {
  doc: number | null
  impl: number | null
  na: boolean
  naReason: string
  overrideReason: string
  responsible: string
  reviewDate: string
  note: string
  evidenceName: string
  evidenceUrl: string
  /** Documentul incarcat legat de raspuns (null = numai nume + link). */
  evidenceFile: EvidenceFile | null
}

/**
 * Formularul deschis pe o cerinta: raspunsul de azi, sau - daca implementarea
 * n-are inca nota - nota propusa din date PRE-SELECTATA. Pre-selectata, nu
 * salvata: omul o confirma cu "Save" (sau alege alta, cu motiv).
 */
export function draftOf(a: AssessmentAnswer | null, proposed: number | null): AnswerDraft {
  return {
    doc: a?.doc_score ?? null,
    impl: a?.impl_score ?? (a?.not_applicable ? null : proposed),
    na: a?.not_applicable ?? false,
    naReason: a?.na_reason ?? '',
    overrideReason: a?.override_reason ?? '',
    responsible: a?.responsible ?? '',
    reviewDate: a?.review_date ?? '',
    note: a?.note ?? '',
    evidenceName: a?.evidence_name ?? '',
    evidenceUrl: a?.evidence_url ?? '',
    evidenceFile: a?.evidence_file ?? null,
  }
}

/** Campurile trimise la `POST /assessment/answer`. */
export function answerFields(code: string, d: AnswerDraft, proposed: number | null): Record<string, PostValue> {
  return {
    code,
    doc_score: d.na || d.doc === null ? '' : d.doc,
    impl_score: d.na || d.impl === null ? '' : d.impl,
    not_applicable: d.na ? 1 : 0,
    na_reason: d.na ? d.naReason : '',
    impl_proposed: d.na || proposed === null ? '' : proposed,
    override_reason: !d.na && proposed !== null && d.impl !== null && d.impl !== proposed ? d.overrideReason : '',
    responsible: d.responsible,
    review_date: d.reviewDate,
    note: d.note,
    evidence_name: d.evidenceName,
    evidence_url: d.evidenceUrl,
    evidence_file_id: d.na || d.evidenceFile === null ? '' : d.evidenceFile.id,
  }
}

/** Implementarea aleasa difera de propunere: motivul e obligatoriu. */
export function needsOverrideReason(d: AnswerDraft, proposed: number | null): boolean {
  return !d.na && proposed !== null && d.impl !== null && d.impl !== proposed
}

/** "Revizuit in ultimii 2 ani" - treapta 2 a documentatiei depinde de asta. */
export function reviewIsOld(reviewDate: string | null | undefined, today: Date = new Date()): boolean {
  if (!reviewDate) return false
  const d = new Date(`${reviewDate}T00:00:00`)
  if (Number.isNaN(d.getTime())) return false
  const limit = new Date(today)
  limit.setFullYear(limit.getFullYear() - 2)
  return d < limit
}
